import React, { useState } from 'react';
import BackLink from '../components/BackLink';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { errorMessage, requestAccess } from './api';


type Request = { state: 'idle' | 'sending' | 'sent' } | { state: 'failed'; message: string };

const NoAccessPage: React.FC<{ planId: string }> = ({ planId }) => {
  const [request, setRequest] = useState<Request>({ state: 'idle' });

  const ask = async () => {
    setRequest({ state: 'sending' });
    try {
      await requestAccess(planId);
      setRequest({ state: 'sent' });
    } catch (e) {
      const status = (e as { status?: number } | null)?.status;
      if (status === 429) setRequest({ state: 'failed', message: 'You asked recently. Try again later.' });
      // Access arrived between the page load and the click: the plan will open on reload.
      else if (status === 409) setRequest({ state: 'failed', message: 'You already have access. Reload the page to open the plan.' });
      else setRequest({ state: 'failed', message: errorMessage(e, 'Could not send the request. Try again.') });
    }
  };

  const sent = request.state === 'sent';

  return (
    <PageLayout kind="focus" className="pt-4 lg:pt-6">
      <div className="mx-auto mt-10 max-w-md rounded-xl bg-surface-container-low px-6 py-8 text-center">
        <h1 className="text-lg font-bold text-on-surface">You do not have access to this plan.</h1>
        <p className="mt-2 text-sm text-on-surface-variant" aria-live="polite">
          {sent
            ? 'Request sent. The owner has been notified.'
            : request.state === 'failed'
              ? request.message
              : 'Ask the owner and they get a message with a link to add you.'}
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={ask}
            disabled={sent || request.state === 'sending'}
            className={buttonClass('filled', 'md')}
          >
            {sent && (
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none">
                <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
            {sent ? 'Request sent' : request.state === 'sending' ? 'Sending...' : 'Request access'}
          </button>
          <BackLink to="/node-plans" label="Node Plans" />
        </div>
      </div>
    </PageLayout>
  );
};

export default NoAccessPage;
